import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { camBasis, createWorldObject, updateWorldObject, deleteWorldObject } from '../services/worldObjects';
import { adminPanel, AdminClose } from './adminChrome';

// NpcPlacePanel — place the street's residents, and actually keep it.
//
// This used to nudge local state and POST to /__npc-save. That endpoint is a
// VITE DEV-SERVER middleware: it does not exist in a built app, so on the
// deployed world every move was discarded, and even locally it wrote a file that
// needed a redeploy before anyone else saw it. Position, rotation and size never
// survived — which is the whole reason this now writes to world_objects.
//
// It also no longer dispatches synthetic keydown events. The street camera
// listens for arrows and WASD on window, so a placement handler bound to those
// keys moved the camera and the resident together.
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const panel = adminPanel({
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 214, pointerEvents: 'auto', color: '#ffe9c4', font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.82)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
});
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const arrow = { ...btn, padding: '9px 0' };
const r2 = (n) => Math.round(n * 100) / 100;
const r3 = (n) => Math.round(n * 1000) / 1000;

export default function NpcPlacePanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'place';
  const npcList = useWorldStore((s) => s.npcList);
  const selectedSlug = useWorldStore((s) => s.npcEdit.slug);
  const setNpcEdit = useWorldStore((s) => s.setNpcEdit);
  const objects = useWorldStore((s) => s.worldObjects);
  const dirty = useWorldStore((s) => s.worldObjectDirty);
  const patch = useWorldStore((s) => s.patchWorldObject);
  const addObj = useWorldStore((s) => s.addWorldObject);
  const removeObj = useWorldStore((s) => s.removeWorldObject);
  const markSaved = useWorldStore((s) => s.markWorldObjectSaved);

  const [fine, setFine] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  if (!ON) return null;

  const rosterEntry = npcList.find((n) => n.slug === selectedSlug) || null;
  const cur = objects.find((o) => o.zone_id === 'street' && o.kind === 'resident' && o.ref === selectedSlug) || null;

  // A resident has no row until it is first moved. Seed one from wherever the
  // body currently stands so nudging starts from its real position rather than
  // snapping it to the origin the moment you touch it.
  const ensureRow = () => {
    if (cur || !rosterEntry) return cur;
    const e = { zone_id: 'street', kind: 'resident', ref: rosterEntry.slug, slot: 0,
      pos: rosterEntry.post || [0, 0, 0], rotY: rosterEntry.rotationY || 0, size: rosterEntry.height || 1.8 };
    addObj(e);
    return e;
  };

  const edit = (fn) => {
    const row = cur || ensureRow();
    if (!row) return;
    // addObj assigns the id, so re-read it from the store on the seeding call.
    const id = row.object_id ?? useWorldStore.getState().worldObjects.slice(-1)[0]?.object_id;
    const live = useWorldStore.getState().worldObjects.find((o) => o.object_id === id) || row;
    patch(id, fn(live));
  };

  const step = fine ? 0.1 : 0.5;
  const move = (axis, dir) => edit((o) => {
    const [x, y, z] = o.pos; const d = step * dir;
    if (axis === 'across') return { pos: [r2(x + camBasis.rx * d), y, r2(z + camBasis.rz * d)] };
    if (axis === 'depth') return { pos: [r2(x + camBasis.fx * d), y, r2(z + camBasis.fz * d)] };
    return { pos: [x, r2(y + (fine ? 0.05 : 0.2) * dir), z] };
  });
  const turn = (dir) => edit((o) => ({ rotY: r2(o.rotY + 0.1 * dir) }));
  const resize = (dir) => edit((o) => ({ size: Math.max(0.2, r3(o.size + (fine ? 0.005 : 0.02) * dir)) }));

  const save = async () => {
    if (!cur) return;
    setBusy(true); setMsg('');
    try {
      const body = { zone_id: 'street', kind: 'resident', ref: cur.ref, slot: cur.slot ?? 0, pos: cur.pos, rotY: cur.rotY, size: cur.size };
      const isNew = typeof cur.object_id === 'string';
      const saved = isNew ? await createWorldObject(body) : await updateWorldObject(cur.object_id, body);
      if (saved?.object_id) { patch(cur.object_id, { object_id: saved.object_id }); markSaved(cur.object_id); markSaved(saved.object_id); }
      setMsg('saved');
    } catch (e) {
      setMsg(/500|Failed/.test(e.message) ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  const revert = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      if (typeof cur.object_id === 'number') await deleteWorldObject(cur.object_id);
      removeObj(cur.object_id);   // falls back to the roster.json default
      setMsg('back to default');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  const isDirty = cur && dirty.includes(cur.object_id);

  return (
    <div style={panel}>
      <AdminClose />
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>Place residents</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10, maxHeight: 148, overflowY: 'auto' }}>
        {npcList.length === 0 && <div style={{ opacity: 0.6, fontSize: 11.5 }}>loading roster…</div>}
        {npcList.map((n) => {
          const row = objects.find((o) => o.zone_id === 'street' && o.kind === 'resident' && o.ref === n.slug);
          return (
            <button key={n.slug} onClick={() => setNpcEdit({ slug: n.slug })}
              style={{ ...btn, fontSize: 11.5, padding: '6px 8px', textAlign: 'left',
                background: n.slug === selectedSlug ? '#ffb25e' : 'rgba(255,178,94,0.14)',
                color: n.slug === selectedSlug ? '#1c1206' : '#ffd27d' }}>
              {n.name || n.slug}{row && dirty.includes(row.object_id) ? ' •' : ''}
            </button>
          );
        })}
      </div>

      {!rosterEntry ? (
        <div style={{ opacity: 0.6, fontSize: 11.5 }}>Pick a resident (or click one in the scene) to move it.</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 4 }}>
            <span />
            <button style={arrow} onClick={() => move('depth', 1)}>↑</button>
            <span />
            <button style={arrow} onClick={() => move('across', -1)}>←</button>
            <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>move</div>
            <button style={arrow} onClick={() => move('across', 1)}>→</button>
            <span />
            <button style={arrow} onClick={() => move('depth', -1)}>↓</button>
            <span />
          </div>

          <div style={{ display: 'flex', gap: 5, margin: '8px 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(1)}>⟲ rotate</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(-1)}>rotate ⟳</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(-1)}>− size</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(1)}>size +</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 8px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('height', -1)}>▼ lower</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('height', 1)}>raise ▲</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '4px 0 8px', cursor: 'pointer', opacity: 0.9 }}>
            <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
            fine steps
          </label>

          {cur && (
            <div style={{ opacity: 0.5, fontSize: 10, marginBottom: 8, fontVariantNumeric: 'tabular-nums' }}>
              {cur.pos.map(r2).join(', ')} · rot {r2(cur.rotY)} · {r3(cur.size)}
            </div>
          )}

          <div style={{ display: 'flex', gap: 5 }}>
            <button disabled={busy || !cur}
              style={{ ...btn, flex: 1, background: isDirty ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: isDirty ? '#1c1206' : '#ffd27d' }}
              onClick={save}>{busy ? '…' : isDirty ? 'Save *' : 'Save'}</button>
            <button style={{ ...btn, width: 62 }} onClick={revert} disabled={busy || !cur}>revert</button>
          </div>
          {msg && <div style={{ fontSize: 10.5, marginTop: 8, opacity: 0.85, color: msg === 'saved' ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}
        </>
      )}
    </div>
  );
}
